// ResetPasswordPage.jsx - Supabase Password Recovery Flow
// This page handles the /reset-password route when users click the recovery link from email
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { Clock } from 'lucide-react';

// Minimum password length requirement
const MIN_PASSWORD_LENGTH = 8;

// Shared inline styles for BRIO compliance
const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--brio-white, #fff)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--brio-space-m)',
    fontFamily: 'var(--brio-font-family, Raleway, sans-serif)',
  },
  card: {
    background: 'var(--brio-white, #fff)',
    border: '1px solid var(--brio-border, rgba(0,0,0,0.1))',
    padding: 'var(--brio-space-l)',
    borderRadius: 0,
  },
  heading: {
    fontFamily: 'var(--brio-font-family)',
    fontWeight: 400,
    fontSize: '15px',
    color: 'var(--brio-black, #111)',
    margin: 0,
    marginBottom: 'var(--brio-space-s)',
  },
  label: {
    display: 'block',
    fontSize: '11px',
    fontWeight: 400,
    color: 'var(--brio-grey, #666)',
    marginBottom: '4px',
    fontFamily: 'var(--brio-font-family)',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--brio-border, rgba(0,0,0,0.1))',
    borderRadius: 0,
    fontFamily: 'var(--brio-font-family)',
    fontSize: '13px',
    color: 'var(--brio-black, #111)',
    background: 'var(--brio-white, #fff)',
    outline: 'none',
    boxSizing: 'border-box',
    WebkitAppearance: 'none',
    appearance: 'none',
  },
  primaryBtn: {
    width: '100%',
    padding: '10px 16px',
    background: 'var(--brio-gold, #9F902A)',
    color: 'var(--brio-white, #fff)',
    border: 'none',
    borderRadius: 0,
    fontFamily: 'var(--brio-font-family)',
    fontSize: '13px',
    fontWeight: 400,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'background 0.15s ease',
  },
  textLink: {
    background: 'none',
    border: 'none',
    color: 'var(--brio-gold, #9F902A)',
    fontSize: '11px',
    fontFamily: 'var(--brio-font-family)',
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'none',
  },
  errorBox: {
    marginBottom: 'var(--brio-space-s)',
    padding: '12px',
    background: 'var(--brio-red-light, rgba(147,73,72,0.1))',
    border: '1px solid var(--brio-red, #934948)',
    color: 'var(--brio-red, #934948)',
    fontSize: '11px',
    fontFamily: 'var(--brio-font-family)',
    borderRadius: 0,
  },
  muted: {
    fontSize: '11px',
    color: 'var(--brio-grey, #666)',
    fontFamily: 'var(--brio-font-family)',
    fontWeight: 200,
  },
  fieldGroup: {
    marginBottom: 'var(--brio-space-s)',
  },
  footer: {
    textAlign: 'center',
    fontSize: '9px',
    color: 'var(--brio-grey)',
    marginTop: 'var(--brio-space-m)',
    fontFamily: 'var(--brio-font-family)',
    fontWeight: 200,
  },
};

// Reusable header component
const BrioHeader = ({ subtitle }) => (
  <div style={{ textAlign: 'center', marginBottom: 'var(--brio-space-l)' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: 'var(--brio-space-s)' }}>
      <Clock style={{ width: 36, height: 36, color: 'var(--brio-gold, #9F902A)' }} />
      <h1 style={{ fontSize: '24px', fontWeight: 400, color: 'var(--brio-black, #111)', fontFamily: 'var(--brio-font-family)', margin: 0 }}>BRIO Atlas</h1>
    </div>
    {subtitle && <p style={styles.muted}>{subtitle}</p>}
  </div>
);

export default function ResetPasswordPage() {
  // Session/recovery state
  const [sessionChecked, setSessionChecked] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Check for recovery session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        // Supabase automatically handles the recovery token from the URL
        // and establishes a session when the page loads
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('[ResetPassword] Session error:', sessionError);
          setHasValidSession(false);
          setSessionChecked(true);
          return;
        }

        if (session?.user) {
          console.log('[ResetPassword] Valid recovery session for:', session.user.email);
          setHasValidSession(true);
          setUserEmail(session.user.email || '');
        } else {
          console.log('[ResetPassword] No valid session found');
          setHasValidSession(false);
        }
      } catch (err) {
        console.error('[ResetPassword] Error checking session:', err);
        setHasValidSession(false);
      } finally {
        setSessionChecked(true);
      }
    };

    checkSession();

    // Also listen for auth state changes (recovery token processing)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[ResetPassword] Auth state changed:', event);
      if (event === 'PASSWORD_RECOVERY') {
        setHasValidSession(true);
        setUserEmail(session?.user?.email || '');
        setSessionChecked(true);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Handle password update
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate password length
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError('Password must be at least ' + MIN_PASSWORD_LENGTH + ' characters long.');
      return;
    }

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        setError(updateError.message);
        return;
      }

      // Success - sign out to ensure clean login with new password
      setSuccess(true);

      // Sign out after successful password update
      await supabase.auth.signOut();

    } catch (err) {
      setError(err.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Navigate to login
  const goToLogin = () => {
    window.location.href = '/';
  };

  // Show loading while checking session
  if (!sessionChecked) {
    return (
      <div style={styles.page}>
        <div style={{ textAlign: 'center' }}>
          <Clock className="animate-pulse" style={{ width: 40, height: 40, color: 'var(--brio-gold, #9F902A)', margin: '0 auto var(--brio-space-s)' }} />
          <p style={styles.muted}>Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Show invalid/expired message if no valid session
  if (!hasValidSession) {
    return (
      <div style={styles.page}>
        <div style={{ width: '100%', maxWidth: '384px' }}>
          <BrioHeader />

          {/* Error Card */}
          <div style={{ ...styles.card, textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: 'var(--brio-space-s)' }}>⚠️</div>
            <h2 style={{ ...styles.heading, color: 'var(--brio-red, #934948)' }}>Invalid or Expired Link</h2>
            <p style={{ ...styles.muted, marginBottom: 'var(--brio-space-m)' }}>
              This reset link is invalid or has expired. Please request a new one.
            </p>
            <button
              onClick={goToLogin}
              style={styles.primaryBtn}
              onMouseEnter={(e) => { e.target.style.background = 'var(--brio-gold-hover)'; }}
              onMouseLeave={(e) => { e.target.style.background = 'var(--brio-gold)'; }}
            >
              Back to login
            </button>
          </div>

          <p style={styles.footer}>
            Powered by Supabase Auth with Row Level Security
          </p>
        </div>
      </div>
    );
  }

  // Show success message after password update
  if (success) {
    return (
      <div style={styles.page}>
        <div style={{ width: '100%', maxWidth: '384px' }}>
          <BrioHeader />

          {/* Success Card */}
          <div style={{ ...styles.card, textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: 'var(--brio-space-s)' }}>✓</div>
            <h2 style={{ ...styles.heading, color: 'var(--brio-green, #84A48C)' }}>Password Updated Successfully</h2>
            <p style={{ ...styles.muted, marginBottom: 'var(--brio-space-m)' }}>
              Your password has been updated. You can now sign in with your new password.
            </p>
            <button
              onClick={goToLogin}
              style={styles.primaryBtn}
              onMouseEnter={(e) => { e.target.style.background = 'var(--brio-gold-hover)'; }}
              onMouseLeave={(e) => { e.target.style.background = 'var(--brio-gold)'; }}
            >
              Go to login
            </button>
          </div>

          <p style={styles.footer}>
            Powered by Supabase Auth with Row Level Security
          </p>
        </div>
      </div>
    );
  }

  // Show password reset form
  return (
    <div style={styles.page}>
      <div style={{ width: '100%', maxWidth: '384px' }}>
        <BrioHeader subtitle="Set your new password" />

        {/* Reset Form Card */}
        <div style={styles.card}>
          <h2 style={styles.heading}>Reset Password</h2>

          {userEmail && (
            <p style={{ ...styles.muted, marginBottom: 'var(--brio-space-s)' }}>
              Resetting password for: <strong style={{ fontWeight: 400, color: 'var(--brio-black)' }}>{userEmail}</strong>
            </p>
          )}

          {/* Error Display */}
          {error && (
            <div style={styles.errorBox}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* New Password Input */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={MIN_PASSWORD_LENGTH}
                disabled={loading}
                style={{
                  ...styles.input,
                  ...(loading ? { background: 'var(--brio-grey-light)', cursor: 'not-allowed' } : {}),
                }}
                onFocus={(e) => { e.target.style.borderColor = 'var(--brio-gold)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'var(--brio-border)'; }}
              />
              <p style={{ fontSize: '9px', color: 'var(--brio-grey)', marginTop: '4px', fontFamily: 'var(--brio-font-family)', fontWeight: 200 }}>
                Minimum {MIN_PASSWORD_LENGTH} characters
              </p>
            </div>

            {/* Confirm Password Input */}
            <div style={{ marginBottom: 'var(--brio-space-m)' }}>
              <label style={styles.label}>Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={MIN_PASSWORD_LENGTH}
                disabled={loading}
                style={{
                  ...styles.input,
                  ...(loading ? { background: 'var(--brio-grey-light)', cursor: 'not-allowed' } : {}),
                }}
                onFocus={(e) => { e.target.style.borderColor = 'var(--brio-gold)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'var(--brio-border)'; }}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.primaryBtn,
                ...(loading ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
              }}
              onMouseEnter={(e) => { if (!loading) e.target.style.background = 'var(--brio-gold-hover)'; }}
              onMouseLeave={(e) => { e.target.style.background = 'var(--brio-gold)'; }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin" style={{ width: 16, height: 16 }} viewBox="0 0 24 24">
                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Updating...
                </>
              ) : (
                'Set new password'
              )}
            </button>
          </form>

          {/* Back to Login */}
          <div style={{ textAlign: 'center', marginTop: 'var(--brio-space-s)' }}>
            <button
              type="button"
              onClick={goToLogin}
              style={styles.textLink}
            >
              ← Back to login
            </button>
          </div>
        </div>

        <p style={styles.footer}>
          Powered by Supabase Auth with Row Level Security
        </p>
      </div>
    </div>
  );
}
